import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { LoyaltyProgramApiService } from '@features/loyalty-program/services/loyalty-program-api/loyalty-program-api.service';
import { MembershipFacade } from '@features/membership/facades/membership.facade';
import { MembershipNumberValidator } from '@features/membership/validators/membership-number-validator';
import { getCheckoutFormData } from '@features/transfer-connect-v2/store/selectors/checkout.selectors';
import { TransferConnectV2CheckoutFormData } from '@features/transfer-connect-v2/types';
import { Store } from '@ngrx/store';
import { getAuthUser } from '@rd/core/auth/store/selectors/auth.selectors';
import {
  LoyaltyProgram,
  Membership,
  MembershipType,
  PointsTransferMembership,
  User,
} from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest, Observable, Subject } from 'rxjs';
import {
  filter,
  map,
  switchMap,
  take,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

@Component({
  selector: 'rd-anz-tcv2-membership',
  templateUrl: './membership.component.html',
  styleUrls: ['./membership.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class AnzTCMembershipComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  loyaltyProgramId = this.activatedRoute.snapshot.paramMap.get('programId')!;

  loyaltyProgram$: Observable<LoyaltyProgram | undefined> =
    this.loyaltyProgramFacade.loyaltyProgramsMap$.pipe(
      map((lpMap) => lpMap[this.loyaltyProgramId]),
    );

  error$: Observable<boolean> =
    this.membershipFacade.createOrUpdateMembershipError$;

  loading$: Observable<boolean> = combineLatest([
    this.loyaltyProgramFacade.loadLoyaltyProgramsLoading$,
    this.membershipFacade.loadMembershipsLoading$,
  ]).pipe(
    map((loadingStates) => loadingStates.some((state) => state === true)),
  );

  createOrUpdateMembershipLoading$: Observable<boolean> =
    this.membershipFacade.createOrUpdateMembershipLoading$;

  user$: Observable<User | null> = this.store.select(getAuthUser);
  checkoutForm$: Observable<TransferConnectV2CheckoutFormData> = this.store
    .select(getCheckoutFormData, { id: this.loyaltyProgramId })
    .pipe(filter(isNotNull));

  form: UntypedFormGroup;

  membership$: Observable<PointsTransferMembership | null> = this.loading$.pipe(
    takeWhile((loading: boolean) => loading === true, true),
    switchMap(() =>
      this.membershipFacade.memberships$.pipe(
        map((memberships: Membership[]) => {
          for (const membership of memberships) {
            if (
              membership.loyaltyProgramId === this.loyaltyProgramId &&
              membership.type === MembershipType.PointsTransfer
            ) {
              return membership;
            }
          }
          return null;
        }),
      ),
    ),
  );

  ngOnInit(): void {
    this.form = this.fb.group({
      membershipForm: this.fb.group({
        membershipNumber: this.fb.control(
          '',
          [Validators.required],
          [
            MembershipNumberValidator.validateMembershipNumber(
              this.loyaltyProgramApiService,
              this.loyaltyProgramId,
            ),
          ],
        ),
        firstName: this.fb.control(
          {
            value: '',
            disabled: true,
          },
          [Validators.required],
        ),
        lastName: this.fb.control(
          {
            value: '',
            disabled: true,
          },
          [Validators.required],
        ),
      }),
    });
    this.membership$
      .pipe(
        withLatestFrom(this.user$.pipe(filter(isNotNull))),
        take(1),
        tap(([membership, user]: [Membership | null, User]) => {
          this.form.setValue({
            membershipForm: {
              membershipNumber: membership?.number ?? '',
              firstName: membership?.firstName || user.firstName || '',
              lastName: membership?.lastName || user.lastName || '',
            },
          });
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.membershipFacade.clearMembershipErrors();
  }

  constructor(
    public membershipFacade: MembershipFacade,
    public loyaltyProgramFacade: LoyaltyProgramFacade,
    private fb: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    private store: Store,
    private loyaltyProgramApiService: LoyaltyProgramApiService,
  ) {}

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const membershipFormValue = this.form.getRawValue().membershipForm;
      this.loyaltyProgram$
        .pipe(
          filter(
            (loyaltyProgram: LoyaltyProgram | undefined) =>
              loyaltyProgram !== undefined,
          ),
          take(1),
          withLatestFrom(this.membership$),
          tap(([loyaltyProgram, membership]) => {
            if (membership) {
              this.membershipFacade.updateMembership({
                type: MembershipType.PointsTransfer,
                membershipId: membership.id,
                membership: {
                  lastName: membershipFormValue.lastName,
                  firstName: membershipFormValue.firstName,
                  number: membershipFormValue.membershipNumber,
                },
              });
            } else {
              this.membershipFacade.createMembership({
                type: MembershipType.PointsTransfer,
                membership: {
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  loyalty_program_id: loyaltyProgram?.id!,
                  lastName: membershipFormValue.lastName,
                  firstName: membershipFormValue.firstName,
                  number: membershipFormValue.membershipNumber,
                },
              });
            }
          }),
        )
        .subscribe();
    }
  }

  get backToDetailsPage(): string {
    return `/transfer/checkout/${this.loyaltyProgramId}`;
  }
}
